import { useQuery, useMutation } from "@tanstack/react-query";
import type {
  CreateBuildingRequest,
  GetBuildingsRequest,
  Building,
  UpdateBuildingRequest,
  GetBuildingsCountRequest,
  GetBuildingsCountResponse,
} from "../../../api/client/types/building.types";
import type {
  PaginatedResponse,
  ApiError,
} from "../../../api/client/types/common.types";
import { buildingEndpoints } from "../../../api/endpoints/building";

/**
 * React Query Keys
 * Organized keys help with cache invalidation and devtools debugging.
 */
const buildingQueryKeys = {
  root: ["buildings"] as const,
  list: (params?: GetBuildingsRequest) =>
    [...buildingQueryKeys.root, "list", params] as const,
  detail: (id: string) => [...buildingQueryKeys.root, "detail", id] as const,
  create: () => [...buildingQueryKeys.root, "create"] as const,
  update: (id: string) => [...buildingQueryKeys.root, "update", id] as const,
  count: (params?: GetBuildingsCountRequest) => [...buildingQueryKeys.root, "count", params] as const,
};

/**
 * Hook: useGetBuildings
 * Fetches a paginated list of buildings with optional filters.
 */
export const useGetBuildings = (params?: GetBuildingsRequest) => {
  return useQuery<PaginatedResponse<Building>, ApiError>({
    queryKey: buildingQueryKeys.list(params),
    queryFn: () => buildingEndpoints.getBuildings(params),
  });
};

/**
 * Hook: useGetBuildingById
 * Fetches a single building by ID.
 */
export const useGetBuildingById = (id: string) => {
  return useQuery<Building, ApiError>({
    queryKey: buildingQueryKeys.detail(id),
    queryFn: () => buildingEndpoints.getBuildingById(id),
    enabled: !!id, // Only run if id is provided
  });
};

/**
 * Hook: useCreateBuilding
 * Creates a new building.
 */
export const useCreateBuilding = () => {
  return useMutation<Building, ApiError, CreateBuildingRequest>({
    mutationKey: buildingQueryKeys.create(),
    mutationFn: (data) => buildingEndpoints.createBuilding(data),
  });
};

/**
 * Hook: useUpdateBuilding
 * Updates an existing building.
 */
export const useUpdateBuilding = () => {
  return useMutation<
    Building,
    ApiError,
    { id: string; data: UpdateBuildingRequest }
  >({
    mutationKey: buildingQueryKeys.update("generic"), // key uses 'generic' to avoid dynamic issues
    mutationFn: ({ id, data }) => buildingEndpoints.updateBuilding(id, data),
  });
};

/**
 * Hook: useGetBuildingsCount
 * Fetches the count of buildings with optional filters.
 */
export const useGetBuildingsCount = (params?: GetBuildingsCountRequest) => {
  return useQuery<GetBuildingsCountResponse, ApiError>({
    queryKey: buildingQueryKeys.count(params),
    queryFn: () => buildingEndpoints.getBuildingsCount(params),
  });
};